// absolute imports
import { AppState, AuthorizationParams } from '@auth0/auth0-react';
import { createFileRoute, redirect } from '@tanstack/react-router';

// relative imports
import { buildOrgLoginOptions } from '../../components/shared/utilities/appUtilities';
import {
  getAppConfigByAppId,
  isValidLegacyAppId,
} from '../../data/app-config/legacyAppIdMapping';

export type LoginSearch = {
  appState?: AppState;
  authorizationParams?: AuthorizationParams;
};

export const Route = createFileRoute('/_public/$appId/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    appState: search.appState as AppState,
    authorizationParams: search.authorizationParams as AuthorizationParams,
  }),
  beforeLoad: async ({ search, context: { auth0 }, params }) => {
    const { appId } = params;
    const { appState, authorizationParams } = search;

    // Validate appId
    if (!isValidLegacyAppId(appId)) {
      // Redirect to main login for invalid appIds
      throw redirect({
        to: '/login',
      });
    }

    const { loginWithRedirect } = auth0;
    const defaultReturnTo = '/';

    try {
      // Load the app config for this appId
      const appConfig = await getAppConfigByAppId(appId);

      // Extract branding information
      const logoUrl = appConfig.logoUrl;

      // Prepare custom parameters to pass to Auth0
      const customParams = {
        ...authorizationParams,
        'ext-custom_logo_url': logoUrl,
      };

      const opts = buildOrgLoginOptions({
        appState: {
          returnTo: appConfig.alternateBaseAppPath || defaultReturnTo,
          ...appState,
        },
        authorizationParams: customParams,
      });

      loginWithRedirect(opts);
    } catch (error) {
      // If there's an error loading the app config, redirect to main login
      console.error('Error loading app config for legacy route:', error);
      throw redirect({
        to: '/login',
      });
    }
  },
});
